import { useCallback } from 'react'

import emDash from '~/components/emdash'
import type { EnergyUnit } from '~/data/types/units'
import { energyUnits } from '~/data/types/units'
import { logger } from '~/debug/logger'

import { roundNumber, useNumberFormatter } from './number'
import { type FormatterHook, combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnitsAbbr, useUnitsFormatter } from './units'

const energyFormats: UnitsFormat<EnergyUnit> = {
  wh: {
    long: 'Watt Hour',
    short: 'Wh'
  },
  kwh: {
    long: 'Kilowatt Hour',
    short: 'kWh'
  },
  mwh: {
    long: 'Megawatt Hour',
    short: 'MWh'
  },
  gwh: {
    long: 'Gigawatt Hour',
    short: 'GWh'
  },
  twh: {
    long: 'Terawatt Hour',
    short: 'TWh'
  },
  pwh: {
    long: 'Petawatt Hour',
    short: 'PWh'
  }
}

function isEnergyUnit(unit: string): unit is EnergyUnit {
  return unit in energyFormats
}

export const formatEnergyUnitsAbbr = formatUnitsAbbr(energyFormats)

type EnergyOptions = { reduce?: boolean }

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'kwh',
  omitUnits: false,
  invalidStr: emDash,
  reduce: false
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '', sigFigs: 2 }

export const useEnergyFormatter: FormatterHook<EnergyOptions> = () => {
  const formatNumber = useNumberFormatter()
  const formatUnits = useUnitsFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: energy,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr,
        reduce
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      const initialUnits = units.toLowerCase()
      if (!isEnergyUnit(initialUnits)) return invalidStr
      if (typeof energy !== 'string' && typeof energy !== 'number') return invalidStr

      // Coerce to number. If it's negative return the invalid string.
      let energyNum = parseFloat(`${energy}`)
      if (isNaN(energyNum) || energyNum < 0) return invalidStr

      // If the options specify to do so, reduce the energy to the largest unit that is >= 1.
      // Round to the requested precision (sigFigs) so reducing doesn't discard decimals the
      // caller asked to keep (e.g. revealing 18.847 MWh instead of 18.8 MWh on hover).
      let energyUnits = initialUnits
      if (reduce) {
        const { num, unit } = reduceWattHours(energyNum, energyUnits, sigFigs)
        energyNum = num
        energyUnits = unit
      }

      const energyStr = formatNumber(energyNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return energyStr

      const formattedUnits = formatUnits.energy(energyUnits, longForm ? 'long' : 'short')
      return [energyStr, formattedUnits].join(' ')
    },
    [formatNumber, formatUnits]
  )
}

type ReduceWattHoursOutput = {
  num: number
  unit: EnergyUnit
}

export function reduceWattHours(num: number, unit: EnergyUnit = 'wh', places = 1): ReduceWattHoursOutput {
  if (num >= 1000) {
    const nextUnit = energyUnits[energyUnits.indexOf(unit) + 1]
    if (nextUnit) return reduceWattHours(num / 1000, nextUnit, places)
  }

  num = roundNumber(num, places)
  return { num, unit }
}

export function convertWattHours(num: number, currentUnit: EnergyUnit, desiredUnit: EnergyUnit): number {
  const currentUnitIndex = energyUnits.indexOf(currentUnit)
  const desiredUnitIndex = energyUnits.indexOf(desiredUnit)

  if (currentUnitIndex === -1 || desiredUnitIndex === -1) {
    logger.error(`Invalid Energy Unit: Current: ${currentUnit} Desired: ${desiredUnit}`)
    return num
  }

  const diff = desiredUnitIndex - currentUnitIndex
  const convertedNum = num / Math.pow(1000, diff)
  return convertedNum
}
